import React, { useState, useEffect } from 'react'
import {
  Typography, Box, Button, Alert, CircularProgress,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { gql, useQuery, useMutation } from '@apollo/client'
import PayoutConfigForm from '../components/PayoutConfig/PayoutConfigForm'
import {
  getRestaurantPayoutConfig,
  saveRestaurantPayoutConfig,
} from '../apollo/mutations'
import { driverPayTheme, DP } from './driverPayTheme'

const GET = gql`${getRestaurantPayoutConfig}`
const SAVE = gql`${saveRestaurantPayoutConfig}`

const NUM_FIELDS = [
  'opt1HourlyRate', 'opt2HourlyRate', 'opt2PerOrderBonus',
  'opt3FlatFeePerOrder', 'opt4BaseFee', 'opt4PerMileRate',
  'opt5PercentOfDeliveryFee', 'opt6MinimumPerDelivery', 'opt8BonusPerExtraOrder',
]
const BOOL_FIELDS = [
  'opt1Enabled', 'opt2Enabled', 'opt3Enabled', 'opt4Enabled',
  'opt5Enabled', 'opt6Enabled', 'opt8Enabled',
]
// ─── Presentation ───
const PAGE = {
  minHeight: { xs: 'calc(100vh - 80px)', sm: '100vh' }, bgcolor: DP.page, color: DP.text,
  fontFamily: DP.font, fontSize: 15, lineHeight: 1.45
}
const content = maxWidth => ({
  maxWidth, mx: 'auto', boxSizing: 'content-box',
  px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4.5 }, pb: { xs: 5, md: 7 },
  display: 'flex', flexDirection: 'column', gap: 3
})
const PAGE_TITLE = { fontWeight: 800, fontSize: { xs: 26, sm: 30 }, letterSpacing: '-0.02em', lineHeight: 1.2, color: DP.text }
const PAGE_DESC = { fontSize: 15, lineHeight: 1.5, color: DP.body, mt: '6px' }

function pickInput(cfg) {
  const out = {}
  BOOL_FIELDS.forEach(k => { if (cfg?.[k] != null) out[k] = !!cfg[k] })
  NUM_FIELDS.forEach(k => {
    if (cfg?.[k] != null && cfg[k] !== '') {
      const n = parseFloat(cfg[k])
      if (Number.isFinite(n)) out[k] = n
    }
  })
  return out
}

export default function MyDriverPayouts() {
  // Restaurant panel: auto-scopes to the logged-in vendor's restaurant.
  const restaurantId = localStorage.getItem('restaurantId')

  const { data, loading, error, refetch } = useQuery(GET, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
  })
  const [cfg, setCfg] = useState(null)
  const [saved, setSaved] = useState(false)
  const [save, { loading: saving, error: saveError }] = useMutation(SAVE, {
    onCompleted: () => { setSaved(true); refetch() },
  })

  useEffect(() => {
    if (data?.restaurantPayoutConfig) setCfg(data.restaurantPayoutConfig)
  }, [data])

  if (!restaurantId) {
    return (
      <ThemeProvider theme={driverPayTheme}>
        <Box sx={PAGE}>
          <Box sx={content(860)}>
            <Alert severity="error">No restaurant context. Please re-login.</Alert>
          </Box>
        </Box>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={driverPayTheme}>
      <Box sx={PAGE}>
        <Box sx={content(860)}>
          <Box component="header">
            <Typography variant="h5" component="h1" sx={PAGE_TITLE}>
              Driver Payouts
            </Typography>
            <Typography variant="body2" sx={PAGE_DESC}>
              Configure how your in-house staff drivers are paid for each delivery and per shift.
              Options can be combined — every enabled option adds on top. Tip is always passed
              through to the driver at 100%.
            </Typography>
          </Box>

          {loading && !data && <CircularProgress />}
          {error && <Alert severity="error">{error.message}</Alert>}
          {saveError && <Alert severity="error">{saveError.message}</Alert>}
          {saved && <Alert severity="success" onClose={() => setSaved(false)}>Saved.</Alert>}

          {cfg && (
            <>
              <PayoutConfigForm value={cfg} onChange={setCfg} disabled={saving} appearance="ink" />
              <Box sx={{ pt: 2.5, borderTop: `1px solid ${DP.border}`, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  disabled={saving}
                  onClick={() => {
                    setSaved(false)
                    save({ variables: { restaurantId, input: pickInput(cfg) } })
                  }}
                  sx={{ textTransform: 'capitalize' }}
                >
                  {saving ? 'Saving…' : 'Save settings'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  )
}
